import { Fragment, useState } from "react";
import { useParams } from "react-router";
import { styled } from "@pigment-css/react";
import type { EntityList as EntityListSchema } from "@acto/genui-components-schema";
import { ChevronRightThin } from "@acto/web-common/components";
import { useFlagWithContext } from "@acto/web-common/feature-flags";
import { useTranslations } from "@acto/web-common/locales";
import { ProductOrderBy } from "@acto/gql/graphql";
import {
  HEADER_GAP,
  SECTION_GAP,
  Card,
  Header,
  CardHeadline,
  HoverRowList,
  InsetRowDivider,
  MetaLabel,
  RowBadge,
  RowBody,
  RowChevron,
  RowMeta,
  RowTitle,
  RowValue,
  Section,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { ShellOverlay } from "@acto/taskpane/field-sales-2/shell";
import {
  PeerPotentialsDrilldown,
  type PeerPotentialsTarget,
} from "@acto/taskpane/field-sales-2/components/patterns/peer-potentials-drilldown";
import {
  ProductDrillOverlay,
  type ProductDrillTarget,
} from "@acto/taskpane/field-sales-2/components/patterns/product-drilldown";
import {
  HiddenProductsDisclosure,
  useHiddenOnCard,
} from "@acto/taskpane/field-sales-2/components/patterns/product-hide";
import { entityListRows, type EntityRow } from "@acto/taskpane/field-sales-2/utils/entityListRows";
import { isProductHideable } from "@acto/taskpane/field-sales-2/utils/productHide";
import { DOMAIN_KPI } from "./domainKpi";
import { MEETING_PREP_ROW_PADDING_BLOCK, MeetingPrepRowItem } from "./rowPrimitives";
import { SentimentColor } from "./SentimentColor";

const HiddenProductsSlot = styled("div")(({ theme }) => ({
  marginTop: `calc(${theme.fieldSalesTwo.spacing[HEADER_GAP]} + ${theme.fieldSalesTwo.spacing[8]} - ${theme.fieldSalesTwo.spacing[SECTION_GAP]})`,
}));

const HiddenProductsDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[8],
  marginBottom: `calc(${theme.fieldSalesTwo.spacing[8]} - ${theme.fieldSalesTwo.spacing[MEETING_PREP_ROW_PADDING_BLOCK]})`,
}));

type OpenDrill = { label: string } & (
  | { kind: "peerPotentials"; target: PeerPotentialsTarget }
  | { kind: "productKpis"; target: ProductDrillTarget }
);

interface EntityListProps {
  part: EntityListSchema;
}

export function EntityList({ part }: EntityListProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep");
  const { id: clientId } = useParams<{ id: string }>();
  const isPeerDrillEnabled = useFlagWithContext("field-sales-2-peer-drill", false);
  const [openDrill, setOpenDrill] = useState<OpenDrill | null>(null);
  const { isHidden, disclosure } = useHiddenOnCard(part);
  const canHideProducts = isProductHideable(part);
  const items = entityListRows(part).filter((item) => !isHidden(item.productIds));

  const drillFor = (item: EntityRow): OpenDrill | null => {
    const drill = item.drill;
    if (!drill || !clientId) return null;

    switch (drill.kind) {
      case "peerPotentials":
        if (!isPeerDrillEnabled) return null;

        return {
          kind: "peerPotentials",
          label: item.title,
          target: {
            clientId,
            productIds: drill.productIds,
            name: item.title,
            level: drill.level,
            eyebrow: part.label ?? t("reasoningMeta"),
            potentialText: item.value?.text ?? null,
            potentialSentiment: item.value?.sentiment ?? null,
          },
        };
      case "productKpis":
        return {
          kind: "productKpis",
          label: item.title,
          target: {
            clientId,
            eyebrow: part.label ?? t("reasoningMeta"),
            label: item.title,
            parentGroupIds: drill.productGroupIds,
            kpi: DOMAIN_KPI[drill.kpi],
            dateWindow: drill.dateRange,
            order: drill.trend === "growth" ? ProductOrderBy.LargestGrowth : ProductOrderBy.LargestDecline,
          },
        };
      default:
        return null;
    }
  };

  const closeDrill = () => setOpenDrill(null);

  return (
    <>
      {openDrill && (
        <ShellOverlay id="entityListDrill" label={openDrill.label} onDismiss={closeDrill}>
          {openDrill.kind === "peerPotentials" ? (
            <PeerPotentialsDrilldown target={openDrill.target} onClose={closeDrill} canHideProducts={canHideProducts} />
          ) : (
            <ProductDrillOverlay target={openDrill.target} onClose={closeDrill} />
          )}
        </ShellOverlay>
      )}
      <Card bleed>
        <Section>
          <Header>
            {part.label && <MetaLabel>{part.label}</MetaLabel>}
            <CardHeadline>{part.headline}</CardHeadline>
          </Header>
          {disclosure.products.length > 0 && (
            <HiddenProductsSlot>
              <HiddenProductsDisclosure {...disclosure} />
            </HiddenProductsSlot>
          )}
          {disclosure.products.length > 0 && items.length > 0 && <HiddenProductsDivider />}
          <HoverRowList role="list">
            {items.map((item, index) => {
              const drill = drillFor(item);

              return (
                <Fragment key={index}>
                  <MeetingPrepRowItem clickable={!!drill} onClick={drill ? () => setOpenDrill(drill) : undefined}>
                    {item.chip && <RowBadge tone={item.chip.sentiment ?? "neutral"}>{item.chip.text}</RowBadge>}
                    <RowBody>
                      <RowTitle>{item.title}</RowTitle>
                      {item.subtitle && <RowMeta>{item.subtitle}</RowMeta>}
                      {item.value && (
                        <RowValue>
                          <SentimentColor sentiment={item.value.sentiment ?? "neutral"}>
                            {item.value.text}
                          </SentimentColor>
                        </RowValue>
                      )}
                    </RowBody>
                    {drill && (
                      <RowChevron>
                        <ChevronRightThin strokeWidth={1.5} />
                      </RowChevron>
                    )}
                  </MeetingPrepRowItem>
                  {index < items.length - 1 && (item.chip ? <InsetRowDivider /> : <Divider />)}
                </Fragment>
              );
            })}
          </HoverRowList>
        </Section>
      </Card>
    </>
  );
}
